import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { footerData } from '../data/mock';
import { ArrowUpRight, Twitter, Linkedin, Instagram } from 'lucide-react';

const socialLinks = {
  Twitter: 'https://twitter.com/OmniSportsGroup',
  LinkedIn: 'https://linkedin.com/company/omnisportsgroup',
  Instagram: 'https://instagram.com/omnisportsgroup'
};

const SocialIcon = ({ platform }) => {
  switch (platform) {
    case 'Twitter':
      return <Twitter size={20} />;
    case 'LinkedIn':
      return <Linkedin size={20} />;
    case 'Instagram':
      return <Instagram size={20} />;
    default:
      return null;
  }
};

import { BrandedProductName } from './BrandedProductName';

// Helper component for footer links
const FooterLink = ({ item, showArrow = false, isProduct = false }) => {
  const isExternal = item.external || item.href.startsWith('mailto:') || item.href.startsWith('http');
  const isHashLink = item.href.includes('#');
  const isComingSoon = item.comingSoon;
  
  const baseClasses = "transition-colors flex items-center gap-1 group";
  const activeClasses = isComingSoon 
    ? "text-white/40 cursor-default" 
    : "text-white/60 hover:text-[#D3FF62]";

  const displayName = isProduct ? <BrandedProductName product={item.name} /> : item.name;

  const content = (
    <>
      {displayName}
      {item.comingSoon && (
        <span className="text-[10px] px-1.5 py-0.5 bg-white/10 text-white/50 rounded-full ml-1">
          Soon
        </span>
      )}
      {item.note && (
        <span className="text-[10px] text-white/40 block">
          {item.note}
        </span>
      )}
      {showArrow && !isComingSoon && (
        <ArrowUpRight 
          size={14} 
          className="opacity-0 group-hover:opacity-100 transition-opacity" 
        />
      )}
    </>
  );

  if (isComingSoon) {
    return <span className={`${baseClasses} ${activeClasses}`}>{content}</span>;
  }

  if (isExternal) {
    return (
      <a href={item.href} className={`${baseClasses} ${activeClasses}`} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  if (isHashLink && !item.href.startsWith('/')) {
    return (
      <a href={item.href} className={`${baseClasses} ${activeClasses}`}>
        {content}
      </a>
    );
  }

  return (
    <Link to={item.href} className={`${baseClasses} ${activeClasses}`} onClick={() => window.scrollTo(0, 0)}>
      {content}
    </Link>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-[#0A0A0A] border-t border-white/10 pt-20 pb-12">
      {/* Main Footer */}
      <div className="container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" onClick={() => window.scrollTo(0, 0)} className="inline-block mb-6">
              <img 
                src="/logo-full.png" 
                alt="OmniSportsGroup" 
                className="h-10 md:h-12 w-auto"
              />
            </Link>
            <p className="text-white/60 body-medium mb-2 max-w-sm">
              {footerData.tagline}
            </p>
            <p className="text-white/40 text-sm italic mb-6 max-w-sm">
              Built at the Intersection of Sports & Technology
            </p>

            {/* Social Links */}
            <div className="flex gap-4">
              {footerData.social.map((platform) => (
                <motion.a
                  key={platform}
                  href={socialLinks[platform]}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/70 hover:bg-[#D3FF62] hover:text-[#004534] transition-colors"
                >
                  <SocialIcon platform={platform} />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Ventures */}
          <div>
            <h4 className="text-white font-semibold mb-4">Ventures</h4>
            <ul className="space-y-3">
              {footerData.links.ventures.map((item) => (
                <li key={item.name}>
                  <FooterLink item={item} showArrow={true} isProduct={true} />
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-4">Company</h4>
            <ul className="space-y-3">
              {footerData.links.company.map((item) => (
                <li key={item.name}>
                  <FooterLink item={item} />
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-white font-semibold mb-4">Resources</h4>
            <ul className="space-y-3">
              {footerData.links.resources.map((item) => (
                <li key={item.name}>
                  <FooterLink item={item} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm">
            © {currentYear} OmniSportsGroup. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" onClick={() => window.scrollTo(0, 0)} className="text-white/50 hover:text-white text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" onClick={() => window.scrollTo(0, 0)} className="text-white/50 hover:text-white text-sm transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
